import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  updatePassword,
  AuthUser,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';
import { config } from '../config';
import {
  ADMIN_IDLE_TIMEOUT_MS,
  IDLE_SIGN_OUT_MESSAGE,
  SESSION_LIMIT_SIGN_OUT_MESSAGE,
  clearCognitoTokens,
  sessionDeadline,
  watchDeadline,
  watchIdle,
} from '../utils/sessionPolicy';

// Configure Amplify (only if credentials are provided)
const isCognitoConfigured = Boolean(config.cognito.userPoolId && config.cognito.userPoolClientId);
const allowMockAuth = config.environment === 'dev';

if (isCognitoConfigured) {
  try {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.cognito.userPoolId,
          userPoolClientId: config.cognito.userPoolClientId,
        },
      },
    });
    // Tokens live per tab: closing the tab ends the session, and the refresh
    // token is not left in localStorage for any script on the origin to read.
    cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);
    clearCognitoTokens(window.localStorage);
  } catch (error) {
    console.error('Failed to configure Amplify:', error);
  }
}

interface User {
  email: string;
  name: string;
  sub: string;
  /** Cognito groups from the ACCESS token (admin, admin-operator, admin-auditor). */
  roles: string[];
}

export interface TotpSetupDetails {
  sharedSecret: string;
  setupUri: string;
}

export type SignInFlowResult =
  | { step: 'DONE' }
  | { step: 'NEW_PASSWORD_REQUIRED' }
  | { step: 'TOTP_SETUP'; totpSetup: TotpSetupDetails }
  | { step: 'TOTP_CODE' };

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<SignInFlowResult>;
  signOut: () => Promise<void>;
  completeNewPassword: (newPassword: string, email: string) => Promise<SignInFlowResult>;
  confirmTotpCode: (code: string, email: string) => Promise<SignInFlowResult>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  hasRole: (...groups: string[]) => boolean;
  isAuthenticated: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
type CognitoSignInResult = Awaited<ReturnType<typeof amplifySignIn>> | Awaited<ReturnType<typeof confirmSignIn>>;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null);

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    return watchIdle({
      timeoutMs: ADMIN_IDLE_TIMEOUT_MS,
      onIdle: () => {
        void endSession({ global: false }).then(() => setError(IDLE_SIGN_OUT_MESSAGE));
      },
    });
  }, [user]);

  useEffect(() => {
    if (!user || deadline === null) return undefined;
    return watchDeadline({
      deadline,
      onExpire: () => {
        void endSession({ global: false }).then(() => setError(SESSION_LIMIT_SIGN_OUT_MESSAGE));
      },
    });
  }, [user, deadline]);

  const checkSession = async () => {
    if (!isCognitoConfigured) {
      if (!allowMockAuth) {
        console.error('Admin Cognito is not configured for this environment');
        setError('Admin authentication is not configured for this environment');
        setIsLoading(false);
        return;
      }

      console.warn('Admin Cognito is not configured, using dev mock authentication');
      const session = localStorage.getItem('cms_session');
      if (session) {
        try {
          const userData = JSON.parse(session);
          setUser({ roles: ['admin'], ...userData });
        } catch {
          localStorage.removeItem('cms_session');
        }
      }
      setIsLoading(false);
      return;
    }

    try {
      const authUser: AuthUser = await getCurrentUser();
      const session = await fetchAuthSession();

      const userAttributes = session.tokens?.idToken?.payload;
      const accessGroups = session.tokens?.accessToken?.payload['cognito:groups'];
      setDeadline(sessionDeadline(session.tokens?.accessToken?.payload.auth_time));

      setUser({
        email: (userAttributes?.email as string) || authUser.signInDetails?.loginId || '',
        name: (userAttributes?.name as string) || (userAttributes?.email as string)?.split('@')[0] || 'User',
        sub: authUser.userId,
        roles: Array.isArray(accessGroups) ? accessGroups.map(group => String(group)) : [],
      });
    } catch {
      // User is not authenticated
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const resolveSignInResult = async (result: CognitoSignInResult, email: string): Promise<SignInFlowResult> => {
    if (result.isSignedIn) {
      await checkSession();
      return { step: 'DONE' };
    }

    if (!result.nextStep) {
      throw new Error('Unable to complete sign in');
    }

    switch (result.nextStep.signInStep) {
      case 'DONE':
        await checkSession();
        return { step: 'DONE' };
      case 'CONFIRM_SIGN_UP':
        throw new Error('Please confirm your email address before signing in');
      case 'RESET_PASSWORD':
        throw new Error('You need to reset your password');
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        return { step: 'NEW_PASSWORD_REQUIRED' };
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
        return {
          step: 'TOTP_SETUP',
          totpSetup: {
            sharedSecret: result.nextStep.totpSetupDetails.sharedSecret,
            setupUri: result.nextStep.totpSetupDetails.getSetupUri('DonateMate CMS', email).toString(),
          },
        };
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        return { step: 'TOTP_CODE' };
      default:
        throw new Error(`Additional step required: ${result.nextStep.signInStep}`);
    }
  };

  const signIn = async (email: string, password: string): Promise<SignInFlowResult> => {
    setError(null);

    if (!isCognitoConfigured) {
      if (!allowMockAuth) {
        throw new Error('Admin authentication is not configured for this environment');
      }

      console.warn('Admin Cognito is not configured, using dev mock authentication');
      const mockUser = {
        email,
        name: email.split('@')[0],
        sub: 'mock-user-id',
        roles: ['admin'],
      };
      localStorage.setItem('cms_session', JSON.stringify(mockUser));
      setUser(mockUser);
      return { step: 'DONE' };
    }

    try {
      const result = await amplifySignIn({
        username: email,
        password,
      });

      return await resolveSignInResult(result, email);
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const completeNewPassword = async (newPassword: string, email: string): Promise<SignInFlowResult> => {
    setError(null);
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });
      return await resolveSignInResult(result, email);
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const confirmTotpCode = async (code: string, email: string): Promise<SignInFlowResult> => {
    setError(null);
    try {
      const result = await confirmSignIn({ challengeResponse: code.replace(/\s/g, '') });
      return await resolveSignInResult(result, email);
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    setError(null);
    try {
      await updatePassword({ oldPassword, newPassword });
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * An explicit sign-out is global: it revokes this admin's refresh tokens on
   * every device. Idle and age expiry only clear this tab's tokens, without
   * revoking anything: a duplicated tab holds a copy of the same refresh
   * token, so revoking it would end a session in active use elsewhere.
   */
  const endSession = async ({ global }: { global: boolean }) => {
    if (!isCognitoConfigured) {
      localStorage.removeItem('cms_session');
      setUser(null);
      return;
    }

    try {
      if (global) {
        // Global sign-out needs a live access token, which may have expired
        // while the tab sat open, so refresh the session first.
        await fetchAuthSession().catch(() => undefined);
        await amplifySignOut({ global: true });
      } else {
        clearCognitoTokens(window.sessionStorage);
      }
    } catch (err) {
      console.error('Sign out error:', err);
    } finally {
      // Clear user state even if Cognito sign-out fails
      setUser(null);
      setDeadline(null);
    }
  };

  const signOut = () => endSession({ global: true });

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        signIn,
        signOut,
        completeNewPassword,
        confirmTotpCode,
        changePassword,
        hasRole: (...groups: string[]) => groups.some(group => user?.roles.includes(group) ?? false),
        isAuthenticated: !!user,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// Helper function to get user-friendly error messages
function getAuthErrorMessage(error: any): string {
  const code = error?.name || error?.code || '';

  switch (code) {
    case 'UserNotFoundException':
    case 'NotAuthorizedException':
      return 'Invalid email or password';
    case 'UserNotConfirmedException':
      return 'Please confirm your email address before signing in';
    case 'PasswordResetRequiredException':
      return 'You need to reset your password';
    case 'TooManyRequestsException':
      return 'Too many attempts. Please try again later';
    case 'InvalidPasswordException':
      return 'Password does not meet requirements';
    case 'InvalidParameterException':
      return 'Invalid email or password format';
    case 'NetworkError':
      return 'Network error. Please check your connection';
    default:
      return error?.message || 'Authentication failed. Please try again';
  }
}
